import { useRef, useSyncExternalStore } from "react";

export type ColumnWidthSource = {
	ref: (node: HTMLElement | null) => void;
	subscribe: (onStoreChange: () => void) => () => void;
	getWidth: () => number | undefined;
};

/**
 * Watches an element's width. Pair with `useColumnWidth`: a subscriber re-renders only when the
 * value it selects changes, so a resize inside the same band does not render the page again.
 * `select` must return a primitive.
 */
export const useColumnWidthSource = (): ColumnWidthSource => {
	const widthRef = useRef<number | undefined>(undefined);
	const sourceRef = useRef<ColumnWidthSource | undefined>(undefined);
	if (!sourceRef.current) {
		const listeners = new Set<() => void>();
		let observer: ResizeObserver | undefined;
		const publish = (width: number) => {
			if (widthRef.current === width) return;
			widthRef.current = width;
			for (const listener of listeners) listener();
		};
		sourceRef.current = {
			ref(node) {
				observer?.disconnect();
				observer = undefined;
				if (!node) return;
				publish(node.getBoundingClientRect().width);
				observer = new ResizeObserver(([entry]) => {
					if (!entry) return;
					publish(entry.contentRect.width);
				});
				observer.observe(node);
			},
			subscribe(onStoreChange) {
				listeners.add(onStoreChange);
				return () => {
					listeners.delete(onStoreChange);
				};
			},
			getWidth: () => widthRef.current,
		};
	}
	return sourceRef.current;
};

export const useColumnWidth = <T>(
	source: ColumnWidthSource,
	select: (width: number | undefined) => T,
): T => {
	const selectRef = useRef(select);
	selectRef.current = select;
	return useSyncExternalStore(
		source.subscribe,
		() => selectRef.current(source.getWidth()),
		() => selectRef.current(undefined),
	);
};

/** A source stuck at one width. Tests and fixtures use this instead of a measured element. */
export const fixedColumnWidth = (width: number | undefined): ColumnWidthSource => ({
	ref: () => {},
	subscribe: () => () => {},
	getWidth: () => width,
});
